import {
  applyInteractiveMetadataUpdate,
  attachSupportQuickActionsWhenMissing,
  registerDismissedInteractiveMessageIds,
  resolveStreamingTargetMessageId,
  retainOnlyLatestInteractiveMetadataForAllFields,
} from '@/components/chat/chat-helpers'
import { learningKeys } from '@/hooks/use-learning'
import { authenticatedFetch } from '@/lib/authenticated-fetch'
import { useChatStore } from '@/stores/chat-store'
import type { ChatMessage } from '@/stores/chat-store'
import type {
  MessageMatchData,
  ResumeContent,
  ResumeContentUpdatedEvent,
  RevisionReadyEvent,
} from '@/types'
import type {
  LearningContentEvent,
  LearningProgressUpdatedEvent,
  LearningTopicGeneratingEvent,
} from '@/types/learning'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useRef } from 'react'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

function getDisplayReadyContent(content: string, revealAll = false): string {
  if (revealAll) return content
  const paragraphBoundary = content.lastIndexOf('\n\n')
  if (paragraphBoundary === -1) return ''
  return content.slice(0, paragraphBoundary).trim()
}

// Resume preview data attached to assistant messages when AI shows preview
interface ResumePreviewData {
  document_id: number
  focus_areas: string[]
  message_id: number
}

export function useChatStreaming() {
  const streamingIdRef = useRef<string | number | undefined>(undefined)
  const userScrolledUpRef = useRef(false)
  const queryClient = useQueryClient()

  const {
    chatId,
    isSending,
    setMessages,
    setIsSending,
    setPreviewPanelOpen,
    setResumeContent,
    setUserDocumentId,
    setIsUpdatingPreview,
    setLearningPanelOpen,
    setLearningContent,
    setIsLoadingLearning,
    setLearningError,
    setLearningTopicGenerating,
  } = useChatStore()

  const sendMessageStreaming = useCallback(
    async (
      userMessageContent: string,
      documentIds?: number[],
      matchData?: MessageMatchData,
      msgMetadata?: Record<string, unknown>,
      options?: { skipOptimisticUser?: boolean },
    ) => {
      if ((!userMessageContent.trim() && !documentIds?.length) || !chatId || isSending) return

      setIsSending(true)
      userScrolledUpRef.current = false

      if (!options?.skipOptimisticUser) {
        // Add optimistic user message
        const optimisticUserMessage: ChatMessage = {
          id: Date.now(),
          role: 'user',
          content: userMessageContent,
          created_at: new Date().toISOString(),
          isNew: true,
          ...(matchData && { matchData }),
        }
        setMessages((prev) => [...prev, optimisticUserMessage])
      }

      // Add placeholder streaming message
      const streamingMessageId = Date.now() + 1
      streamingIdRef.current = streamingMessageId
      const streamingMessage: ChatMessage = {
        id: streamingMessageId,
        role: 'assistant',
        content: '',
        created_at: new Date().toISOString(),
        isStreaming: true,
      }
      setMessages((prev) => {
        const withStreaming = [...prev, streamingMessage]
        return retainOnlyLatestInteractiveMetadataForAllFields(withStreaming).messages
      })

      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 120000)
      let resumeGeneratingTimeoutId: ReturnType<typeof setTimeout> | null = null
      try {
        const response = await authenticatedFetch(
          `${API_URL}/api/v1/chat/${chatId}/messages/stream`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'text/event-stream',
            },
            body: JSON.stringify({
              role: 'user',
              content: userMessageContent,
              ...(documentIds?.length && { document_ids: documentIds }),
              ...(msgMetadata && { msg_metadata: msgMetadata }),
            }),
            signal: controller.signal,
            replayAfterRefresh: false,
          },
        )

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`)
        }

        const reader = response.body?.getReader()
        if (!reader) {
          throw new Error('No reader available')
        }

        const decoder = new TextDecoder()
        let buffer = ''
        let fullResponse = ''
        let displayedContent = ''
        let pendingRevisionData: RevisionReadyEvent | null = null
        const STALL_TIMEOUT_NORMAL = 30000
        const STALL_TIMEOUT_PREVIEW = 60000
        let isResumePreviewPhase = false
        let receivedQuickActionsDuringStream = false

        // iMessage-style paragraph reveal
        let revealedParagraphCount = 0

        while (true) {
          const readPromise = reader.read()
          const timeoutPromise = new Promise<never>((_, reject) => {
            const stallTimeoutMs = isResumePreviewPhase
              ? STALL_TIMEOUT_PREVIEW
              : STALL_TIMEOUT_NORMAL
            setTimeout(() => {
              reject(new Error('Stream stalled - no data received'))
            }, stallTimeoutMs)
          })

          const { done, value } = await Promise.race([readPromise, timeoutPromise])
          if (done) break

          // Buffer across reads: a single SSE `data:` line can be split across
          // chunk boundaries, so only parse lines we know are complete (i.e.
          // terminated by '\n') and carry the trailing partial line forward.
          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() || ''

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const event = JSON.parse(line.slice(6))

                if (event.type === 'content') {
                  const text = event.data?.text || ''
                  fullResponse += text
                  const nextDisplayContent = getDisplayReadyContent(fullResponse)
                  if (!nextDisplayContent) {
                    // No complete paragraph yet
                  } else {
                    const paragraphs = nextDisplayContent
                      .split(/\n\s*\n+/)
                      .map((p: string) => p.trim())
                      .filter(Boolean)

                    if (paragraphs.length > revealedParagraphCount) {
                      revealedParagraphCount = paragraphs.length
                      displayedContent = nextDisplayContent
                      setMessages((prev) =>
                        prev.map((msg) =>
                          msg.id === streamingMessageId
                            ? { ...msg, content: displayedContent, isStreaming: true }
                            : msg,
                        ),
                      )
                    }
                  }
                } else if (event.type === 'status') {
                  const statusText = event.data?.text || ''
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === streamingMessageId ? { ...msg, statusText } : msg,
                    ),
                  )
                } else if (event.type === 'revision_ready') {
                  const revisionData = event.data as RevisionReadyEvent
                  pendingRevisionData = revisionData
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === streamingMessageId ? { ...msg, revision: revisionData } : msg,
                    ),
                  )
                } else if (event.type === 'resume_preview') {
                  const previewData = event.data as ResumePreviewData
                  isResumePreviewPhase = true
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === streamingMessageId ? { ...msg, resumePreview: previewData } : msg,
                    ),
                  )
                } else if (event.type === 'resume_generating') {
                  isResumePreviewPhase = true

                  setIsUpdatingPreview(true)
                  setPreviewPanelOpen(true)
                  setLearningPanelOpen(false)
                  // Safety timeout: auto-reset overlay if resume_content_updated never arrives
                  if (resumeGeneratingTimeoutId) clearTimeout(resumeGeneratingTimeoutId)
                  resumeGeneratingTimeoutId = setTimeout(() => {
                    setIsUpdatingPreview(false)
                    setMessages((prev) =>
                      prev.map((msg) =>
                        msg.id === streamingMessageId ? { ...msg, isGeneratingResume: false } : msg,
                      ),
                    )
                  }, 90000) // 90s safety net
                  // Set document ID and existing content immediately so preview panel appears
                  const genData = event.data as {
                    document_id?: number
                    content?: ResumeContent
                  }
                  if (genData?.document_id) {
                    setUserDocumentId(genData.document_id)
                  }
                  if (genData?.content) {
                    setResumeContent(genData.content)
                  }
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === streamingMessageId ? { ...msg, isGeneratingResume: true } : msg,
                    ),
                  )
                } else if (event.type === 'resume_content_updated') {
                  isResumePreviewPhase = false

                  if (resumeGeneratingTimeoutId) {
                    clearTimeout(resumeGeneratingTimeoutId)
                    resumeGeneratingTimeoutId = null
                  }
                  const contentEvent = event.data as ResumeContentUpdatedEvent
                  setResumeContent(contentEvent.content)
                  setUserDocumentId(contentEvent.document_id)
                  setPreviewPanelOpen(true)
                  setLearningPanelOpen(false)
                  setIsUpdatingPreview(false)
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === streamingMessageId ? { ...msg, isGeneratingResume: false } : msg,
                    ),
                  )
                } else if (event.type === 'replace_content') {
                  const replacementText = event.data?.text || ''
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === streamingMessageId ? { ...msg, isGeneratingResume: false } : msg,
                    ),
                  )
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === streamingMessageId ? { ...msg, content: replacementText } : msg,
                    ),
                  )
                  fullResponse = replacementText
                  displayedContent = replacementText
                } else if (event.type === 'resume_error') {
                  isResumePreviewPhase = false

                  if (resumeGeneratingTimeoutId) {
                    clearTimeout(resumeGeneratingTimeoutId)
                    resumeGeneratingTimeoutId = null
                  }
                  setIsUpdatingPreview(false)
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === streamingMessageId ? { ...msg, isGeneratingResume: false } : msg,
                    ),
                  )
                } else if (event.type === 'learning_loading') {
                  setLearningPanelOpen(true)
                  setIsLoadingLearning(true)
                  setLearningError(null)
                  setPreviewPanelOpen(false)
                } else if (event.type === 'learning_topic_generating') {
                  const generatingEvent = event.data as LearningTopicGeneratingEvent
                  setLearningPanelOpen(true)
                  setIsLoadingLearning(true)
                  setPreviewPanelOpen(false)
                  setLearningError(null)
                  setLearningTopicGenerating(generatingEvent)
                  queryClient.invalidateQueries({ queryKey: learningKeys.topics() })
                } else if (event.type === 'learning_content') {
                  const learningEvent = event.data as LearningContentEvent
                  setLearningContent(learningEvent)
                  setIsLoadingLearning(false)
                  setLearningError(null)
                  if (learningEvent.generating_topic_id) {
                    const currentGenerating = useChatStore.getState().learningTopicGenerating
                    if (currentGenerating?.topic_id !== learningEvent.generating_topic_id) {
                      setLearningTopicGenerating({
                        topic_id: learningEvent.generating_topic_id,
                        skill_name: learningEvent.topic_title || 'New skill',
                        title: learningEvent.topic_title || 'Learning path',
                      })
                    }
                  }
                } else if (event.type === 'learning_error') {
                  const errorText =
                    (typeof event.data?.message === 'string' && event.data.message) ||
                    (typeof event.data?.error === 'string' && event.data.error) ||
                    'Failed to load learning content. Please try again.'
                  setLearningPanelOpen(true)
                  setIsLoadingLearning(false)
                  setLearningError(errorText)
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === streamingMessageId
                        ? {
                            ...msg,
                            content: `${msg.content}\n\n_${errorText}_`,
                            isStreaming: false,
                          }
                        : msg,
                    ),
                  )
                } else if (event.type === 'learning_progress_updated') {
                  const progressEvent = event.data as LearningProgressUpdatedEvent
                  setLearningContent((prev) =>
                    prev
                      ? {
                          ...prev,
                          progress: {
                            status: progressEvent.status,
                            progress_percent: progressEvent.progress_percent,
                          },
                        }
                      : prev,
                  )
                } else if (event.type === 'quick_replies') {
                  const options = Array.isArray(event.data?.options)
                    ? (event.data.options.filter(
                        (v: unknown): v is string => typeof v === 'string',
                      ) ?? [])
                    : []
                  const targetMessageId = event.data?.message_id
                  const rawQuickRepliesDocumentId =
                    event.data?.document_id ??
                    event.data?.resume_id ??
                    event.data?.matched_resume_id
                  const quickRepliesDocumentId =
                    typeof rawQuickRepliesDocumentId === 'number'
                      ? rawQuickRepliesDocumentId
                      : Number(rawQuickRepliesDocumentId)

                  if (options.length > 0) {
                    setMessages((prev) => {
                      const targetId = resolveStreamingTargetMessageId(
                        prev,
                        targetMessageId,
                        streamingMessageId,
                      )
                      if (!targetId) return prev

                      const { messages: next, supersededIds } = applyInteractiveMetadataUpdate(
                        prev,
                        targetId,
                        'quick_replies',
                        {
                          quick_replies: options,
                          ...(Number.isFinite(quickRepliesDocumentId) && quickRepliesDocumentId > 0
                            ? { quick_replies_document_id: quickRepliesDocumentId }
                            : {}),
                        },
                      )

                      registerDismissedInteractiveMessageIds(chatId, supersededIds, new Set())

                      return next
                    })
                  }
                } else if (event.type === 'quick_actions') {
                  const actions = Array.isArray(event.data?.actions)
                    ? event.data.actions.filter(
                        (
                          action: unknown,
                        ): action is { label: string; action: string; target: string } =>
                          typeof action === 'object' &&
                          action !== null &&
                          'label' in action &&
                          'action' in action &&
                          typeof (action as { label: unknown }).label === 'string' &&
                          typeof (action as { action: unknown }).action === 'string',
                      )
                    : []
                  const targetMessageId = event.data?.message_id
                  if (actions.length > 0) {
                    receivedQuickActionsDuringStream = true
                    setMessages((prev) => {
                      const targetId = resolveStreamingTargetMessageId(
                        prev,
                        targetMessageId,
                        streamingMessageId,
                      )
                      if (!targetId) return prev

                      const { messages: next, supersededIds } = applyInteractiveMetadataUpdate(
                        prev,
                        targetId,
                        'quick_actions',
                        { quick_actions: actions },
                      )

                      registerDismissedInteractiveMessageIds(chatId, supersededIds, new Set())

                      return next
                    })
                  }
                } else if (event.type === 'preferences_updated') {
                  queryClient.invalidateQueries({ queryKey: ['job-preferences'] })
                } else if (event.type === 'job_fetch_error') {
                  const errorMessage =
                    event.data?.message ||
                    "I couldn't fetch that job posting link. Please try another URL or paste the description."
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === streamingMessageId
                        ? {
                            ...msg,
                            content: errorMessage,
                            isStreaming: false,
                            error: true,
                          }
                        : msg,
                    ),
                  )
                } else if (event.type === 'done') {
                  isResumePreviewPhase = false
                  let finalContent = fullResponse
                  if (pendingRevisionData) {
                    const revisionJson = `\n[system:revision_data]${JSON.stringify(pendingRevisionData)}[/system]`
                    finalContent += revisionJson
                  }

                  const serverUserMsgId = event.data?.user_message_id
                  const serverAsstMsgId = event.data?.assistant_message_id
                  const optimisticUserId = streamingMessageId - 1 // Date.now()

                  setMessages((prev) => {
                    let updated = prev.map((msg) => {
                      if (msg.id === streamingMessageId) {
                        return {
                          ...msg,
                          ...(serverAsstMsgId ? { id: serverAsstMsgId } : {}),
                          content: finalContent,
                          isStreaming: false,
                          isGeneratingResume: false,
                        }
                      }
                      if (serverUserMsgId && msg.id === optimisticUserId) {
                        return { ...msg, id: serverUserMsgId, isNew: false }
                      }
                      return msg
                    })

                    if (!receivedQuickActionsDuringStream) {
                      updated = attachSupportQuickActionsWhenMissing(updated)
                    }

                    const { messages: next, supersededIds } =
                      retainOnlyLatestInteractiveMetadataForAllFields(updated)

                    registerDismissedInteractiveMessageIds(chatId, supersededIds, new Set())

                    return next
                  })
                } else if (event.type === 'error') {
                  const errorMsg = event.data?.message || 'Streaming error'
                  console.error('Streaming error:', event.data)
                  throw new Error(errorMsg)
                }
              } catch (parseError) {
                // JSON.parse failures surface as SyntaxError. These must never
                // reach the user as raw text - skip the malformed line. Any other
                // error (e.g. a deliberately thrown `error`-event) propagates.
                if (!(parseError instanceof SyntaxError)) {
                  throw parseError
                }
                if (line.trim().length > 6) {
                  console.warn('Failed to parse SSE event:', line)
                }
              }
            }
          }
        }
      } catch (error: unknown) {
        console.error('Failed to send streaming message:', error)

        let errorMessage = 'Failed to send message. Please try again.'
        let isRateLimitError = false

        if (error && typeof error === 'object' && 'name' in error && error.name === 'AbortError') {
          errorMessage = 'Request timed out. Please try again.'
        } else if (
          error &&
          typeof error === 'object' &&
          'message' in error &&
          typeof error.message === 'string'
        ) {
          const msg = error.message.toLowerCase()
          if (msg.includes('throttl') || msg.includes('too many') || msg.includes('rate limit')) {
            errorMessage =
              'Our AI service is currently busy. While you wait, you can schedule a call with a career consultant or explore other ways to connect.'
            isRateLimitError = true
          } else if (msg.includes('stalled')) {
            errorMessage =
              "I'm having trouble completing my response right now. Please try again in a moment."
          } else if (msg.includes('401')) {
            errorMessage = 'Session expired. Please log in again.'
          } else {
            errorMessage = error.message
          }
        }

        setMessages((prev) =>
          prev.map((m) =>
            m.id === streamingMessageId
              ? {
                  ...m,
                  content: errorMessage,
                  isStreaming: false,
                  isGeneratingResume: false,
                  error: true,
                  isRateLimitError,
                }
              : m,
          ),
        )
      } finally {
        clearTimeout(timeoutId)
        if (resumeGeneratingTimeoutId) clearTimeout(resumeGeneratingTimeoutId)
        setIsSending(false)
        setIsUpdatingPreview(false)
        streamingIdRef.current = undefined

        // No refetch after streaming - the done handler already sets final
        // content and swaps to server IDs. Refetching caused a flash when
        // the merge replaced the entire message list.
      }
    },
    [
      chatId,
      isSending,
      setMessages,
      setIsSending,
      setPreviewPanelOpen,
      setResumeContent,
      setUserDocumentId,
      setIsUpdatingPreview,
      setLearningPanelOpen,
      setLearningContent,
      setIsLoadingLearning,
      setLearningError,
      setLearningTopicGenerating,
      queryClient,
    ],
  )

  return {
    sendMessageStreaming,
    streamingIdRef,
    userScrolledUpRef,
  }
}
